import { NotificationCard } from '../../components/Card';
import { Center, Flex, Icon, Text } from '@chakra-ui/react';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationTime } from '../../components/Time';
import {
  RequisitionDetailsSection,
  RequisitionDetailsList,
} from '../requisition-notifications/components';
import { decode } from 'js-base64';
import { KButton } from '@/ui-library/components';
import { useHistory } from 'react-router-dom';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';

interface OfferData {
  id?: number;
  url?: string;
  position?: string;
  job_title?: string;
  department?: string;
  start_date?: string;
  reporting_manager?: string;
}

interface CandidateData {
  id?: number;
  name?: string;
  url?: string;
  avatar_url?: string | null;
}

interface ApplicationData {
  id?: number;
  url?: string;
}

interface NotificationData {
  offer?: OfferData;
  candidate?: CandidateData;
  application?: ApplicationData;
  provider?: string;
  account_id?: number;
  event_time?: string;
}

interface Notification {
  id?: string | number;
  custom_attributes?: string;
  notification_type?: number;
  [key: string]: any;
}

interface OfferAcceptedProps {
  notification?: Notification;
  onClose?: () => void;
}

const OfferAccepted = ({
  notification = {},
  onClose = () => {},
}: OfferAcceptedProps) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { custom_attributes } = notification;
  const history = useHistory();
  let data: NotificationData = {};
  try {
    data = JSON.parse(decode(custom_attributes || ''));
  } catch (error) {
    console.error(
      'Error decoding offer accepted notification attributes:',
      error
    );
  }

  const { offer = {}, candidate = {} } = data || {};

  const {
    url: offerUrl,
    position,
    job_title,
    department,
    start_date,
    reporting_manager,
  } = offer;

  const { id: candidateId, name: candidateName } = candidate;

  const detailsData = [
    { label: 'Position', value: position || job_title },
    { label: 'Department', value: department },
    { label: 'Start date', value: start_date },
    { label: 'Reporting manager', value: reporting_manager },
  ].filter((item) => item.value);

  const handleViewOffer = () => {
    if (offerUrl) {
      markAsRead({ id: notification?.id });
      window.open(offerUrl, '_self');
      onClose();
    }
  };

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center minW="34px" h="34px" borderRadius="sm" bg="primary-100">
        <Icon as={LayersThree02} color="primary-500" />
      </Center>
      <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
        <Text>
          <Text as="span" fontSize="sm">
            Great news!{' '}
          </Text>
          {candidateName && (
            <Text as="span" size="sm-semi-bold">
              {candidateName}
            </Text>
          )}
          <Text as="span" fontSize="sm">
            {' '}
            has accepted the offer for{' '}
          </Text>
          {(position || job_title) && (
            <Text as="span" size="sm-semi-bold">
              {position || job_title}
            </Text>
          )}
        </Text>

        <RequisitionDetailsSection title="Offer details">
          <RequisitionDetailsList data={detailsData} />
        </RequisitionDetailsSection>

        <NotificationTime notification={notification} />
        <Flex gap="8px">
          <KButton
            w="fit-content"
            variant="primary"
            borderRadius="8px"
            onClick={handleViewOffer}
            maxH="34px"
            data-test-id="go-to-offer-button"
          >
            Go to offer
          </KButton>
          <KButton
            w="fit-content"
            variant="secondary"
            borderRadius="8px"
            onClick={() => {
              markAsRead({ id: notification?.id });
              history.push(`/candidates/${candidateId}`);
              onClose();
            }}
            maxH="34px"
            data-test-id="view-candidate-profile-button"
          >
            View candidate profile
          </KButton>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const OfferAcceptedNotification = ({
  notification = {},
  onClose = () => {},
}: OfferAcceptedProps) => {
  return (
    <NotificationCard notification={notification}>
      <OfferAccepted notification={notification} onClose={onClose} />
    </NotificationCard>
  );
};
